import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { School, Mail, X, Loader2, CheckCircle, Plus } from 'lucide-react';
import MagneticButton from './MagneticButton';
import { collection, addDoc } from 'firebase/firestore'; 
import { db } from '../firebase'; 

export const CollegeAddon = ({ customTrigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [collegeName, setCollegeName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  
  // Validation State
  const [errorMessage, setErrorMessage] = useState('');
  const [errorField, setErrorField] = useState(null); // 'college' | 'email' | null

  // Lock scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSubmitStatus('idle');
      setCollegeName('');
      setEmail('');
      setErrorMessage('');
      setErrorField(null);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setErrorField(null);

    // 1. Check College Name (Min 3 chars)
    if (!collegeName.trim() || collegeName.trim().length < 3) {
      setErrorField('college');
      setErrorMessage("Please enter a valid college name (min 3 chars).");
      return;
    }

    // 2. Check Email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorField('email');
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'collegeAddons'), {
        collegeName: collegeName,
        email: email,
        timestamp: new Date().toISOString(),
        status: 'pending'
      });
      setSubmitStatus('success');
    } catch (error) {
      // console.error("Error submitting college request:", error);
      setErrorMessage('Connection failed. Please try again.'); 
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to clear error when user types
  const handleInputChange = (field, setter) => (e) => {
    setter(e.target.value);
    // Only clear the error if the user is typing in the field that currently has the error
    if (errorField === field) {
      setErrorField(null);
      setErrorMessage('');
    }
  };

  return (
    <>
      {/* TRIGGER */}
      {customTrigger ? (
        <div onClick={() => setIsOpen(true)} className="cursor-pointer">
          {customTrigger}
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className="group flex items-center gap-2 text-sp-text-secondary hover:text-sp-cyan transition-colors text-sm font-medium mt-4 mx-auto"
        >
          <Plus size={16} />
          <span>Can't find your college? Submit a request</span>
        </button>
      )}

      {/* MODAL */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            >
              <motion.div 
                className="bg-sp-card-bg border border-sp-border p-8 rounded-3xl w-full max-w-md relative shadow-[0_0_40px_rgba(11,217,229,0.15)]"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
              >
                <button onClick={handleClose} className="absolute top-4 right-4 text-sp-text-secondary hover:text-sp-text transition-colors">
                  <X size={24} />
                </button>

                {submitStatus === 'success' ? (
                  <div className="text-center py-6">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-block mb-4">
                      <CheckCircle className="w-16 h-16 text-sp-cyan mx-auto" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-sp-text mb-2">Request Sent!</h3>
                    <p className="text-sp-text-secondary mb-6">Thanks for helping us grow. We'll email you as soon as {collegeName} is live on Spark.</p>
                    <button onClick={handleClose} className="w-full bg-sp-cyan-dim hover:bg-sp-cyan/20 text-sp-cyan font-semibold py-3 rounded-xl transition-all">Close</button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-sp-text mb-2">Add Your College</h3>
                    <p className="text-sp-text-secondary mb-6 text-sm">Don't see your campus? Let us know!</p>
                    
                    {/* ADDED noValidate HERE */}
                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                      {/* College Input */}
                      <div className="relative">
                        <School className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                        <input 
                          type="text" 
                          placeholder="College Name" 
                          value={collegeName} 
                          onChange={handleInputChange('college', setCollegeName)} 
                          className={`w-full bg-[#0f1214] border ${errorField === 'college' ? 'border-sp-cyan' : 'border-gray-800'} text-white pl-12 p-4 rounded-xl focus:outline-none focus:border-sp-cyan focus:ring-1 focus:ring-sp-cyan placeholder:text-gray-500 transition-colors`}
                          disabled={isSubmitting} 
                        />
                      </div>

                      {/* Email Input */}
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                        <input 
                          type="email" 
                          placeholder="Your Email" 
                          value={email} 
                          onChange={handleInputChange('email', setEmail)} 
                          className={`w-full bg-[#0f1214] border ${errorField === 'email' ? 'border-sp-cyan' : 'border-gray-800'} text-white pl-12 p-4 rounded-xl focus:outline-none focus:border-sp-cyan focus:ring-1 focus:ring-sp-cyan placeholder:text-gray-500 transition-colors`}
                          disabled={isSubmitting} 
                        />
                      </div>

                      {/* Error Message (Cyan) */}
                      {errorMessage && (
                        <motion.p 
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-sp-cyan text-sm pl-1"
                        >
                          {errorMessage}
                        </motion.p>
                      )}

                      <MagneticButton>
                        <button type="submit" disabled={isSubmitting} className="w-full bg-sp-cyan hover:bg-sp-cyan/80 text-black font-semibold py-4 text-lg rounded-xl shadow-[0_0_30px_rgba(11,217,229,0.3)] hover:shadow-[0_0_40px_rgba(11,217,229,0.5)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2">
                          {isSubmitting ? <Loader2 className="animate-spin w-5 h-5" /> : <span>Submit Request</span>}
                        </button>
                      </MagneticButton>
                    </form>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};



